import classNames from "classnames";
import { useTranslations } from "providers";

import { LeanIcon } from "./LeanIcon";
import { getTableCellStyle, IColumn, ISortInfo } from "./LeanTable";
import styles from "./LeanTable.module.scss";
import { SortDirection } from "./SortDirection";

const getNextSortDirection = (sortDirection: SortDirection | undefined): SortDirection | undefined => {
  if (sortDirection === undefined) return SortDirection.Ascending;
  if (sortDirection === SortDirection.Ascending) return SortDirection.Descending;
  return undefined;
};

export interface IColumnHeaderProps<TItem> {
  column: IColumn<TItem>;
  columnIndex: number;
  sortDirection?: SortDirection;
  isFilterActive?: boolean;
  openDialog?: (columnIndex: number, rect: DOMRect) => void;
  onSortingChanged?: (sortInfo: ISortInfo | undefined) => void;
  onClearFilter: (columnIndex: number) => void;
}

export const LeanTableColumnHeader = <TItem,>(props: IColumnHeaderProps<TItem>) => {
  const { column, columnIndex, sortDirection, isFilterActive, openDialog, onSortingChanged, onClearFilter } = props;
  const className = classNames(onSortingChanged && styles.sortable, column.headerClassName);
  const i18n = useTranslations();

  return (
    <th key={columnIndex} className={className} style={getTableCellStyle(column.width, column.minWidth)}>
      <div
        className={styles.sbbSortHeaderContainer}
        onClick={
          onSortingChanged
            ? () => {
                const newSortDirection = getNextSortDirection(sortDirection);
                onSortingChanged(newSortDirection && { columnIndex, sortDirection: newSortDirection });
              }
            : undefined
        }
      >
        {column.children ? (
          column.children
        ) : (
          <div className={styles.columnTitle}>{column.titleContent || column.title}</div>
        )}
        {sortDirection === SortDirection.Ascending && (
          <LeanIcon className={styles.sortArrow} icon="ArrowUp" size="32px" />
        )}
        {sortDirection === SortDirection.Descending && (
          <LeanIcon className={styles.sortArrow} icon="ArrowDown" size="32px" />
        )}
        {isFilterActive === true && (
          <LeanIcon
            className={styles.filter_x}
            icon="FilterX"
            size="20px"
            title={i18n.Filter_Clear_Icon_Tooltip}
            onClick={() => onClearFilter(columnIndex)}
          />
        )}
        {openDialog && !isFilterActive && (
          <span
            className={styles.filter}
            title={i18n.Filter_Icon_Tooltip}
            onClick={(e) => {
              e.stopPropagation();
              openDialog(columnIndex, e.currentTarget.getBoundingClientRect());
            }}
          >
            <LeanIcon icon="Filter" size="20px" />
          </span>
        )}
        {openDialog && isFilterActive && (
          <span
            className={styles.filter_settings}
            title={i18n.Filter_Active_Icon_Tooltip}
            onClick={(e) => {
              e.stopPropagation();
              openDialog(columnIndex, e.currentTarget.getBoundingClientRect());
            }}
          >
            <LeanIcon icon="FilterSettings" size="20px" />
          </span>
        )}
      </div>
    </th>
  );
};
